
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ThemeProvider } from "@/components/ui/theme-provider";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/contexts/AuthContext";

import Index from "@/pages/Index";
import Login from "@/pages/Auth/Login";
import Cadastro from "@/pages/Auth/Cadastro";
import Dashboard from "@/pages/Dashboard";
import GeneralDashboard from "@/pages/Dashboard/GeneralDashboard";
import NotFound from "@/pages/NotFound";
import EnergiaDashboard from "@/pages/Energia/EnergiaDashboard";
import EnergiaConfiguracao from "@/pages/Energia/EnergiaConfiguracao";
import MonitoramentoPage from "@/pages/Energia/MonitoramentoPage";
import FinanceiroDashboard from "@/pages/Financeiro/FinanceiroDashboard";
import FinanceiroGeradorPage from "@/pages/Financeiro/FinanceiroGeradorPage";
import FinanceiroFormPage from "@/pages/Financeiro/FinanceiroFormPage";
import FinanceiroRecebimentosPage from "@/pages/Financeiro/FinanceiroRecebimentosPage";
import FinanceiroPagamentosPage from "@/pages/Financeiro/FinanceiroPagamentosPage";
import FinanceiroCadastroPage from "@/pages/Financeiro/FinanceiroCadastroPage";
import FinanceiroConfiguracoesPage from "@/pages/Financeiro/FinanceiroConfiguracoesPage";
import RelatoriosFinanceirosPage from "@/pages/Financeiro/RelatoriosFinanceirosPage";
import GeradoresPage from "@/pages/Geradores/GeradoresPage";
import GeradorNovo from "@/pages/Geradores/GeradorNovo";
import GeradorDetalhes from "@/pages/Geradores/GeradorDetalhes";
import ClientesPage from "@/pages/Clientes/ClientesPage";
import ClienteNovo from "@/pages/Clientes/ClienteNovo";
import ClienteDetalhes from "@/pages/Clientes/ClienteDetalhes";
import ParceirosPage from "@/pages/Parceiros/ParceirosPage";
import ParceiroDetalhes from "@/pages/Parceiros/ParceiroDetalhes";
import UsersPage from "@/pages/Users/UsersPage";
import ProfilePage from "@/pages/Users/ProfilePage";
import InverterApiPage from "@/pages/Energia/InverterApiPage";
import SolarDashboard from "@/pages/Solar/SolarDashboard";
import FluxoSolar from "@/pages/Solar/FluxoSolar";
import CalculadoraSolarPage from "@/pages/Calculadora/CalculadoraSolarPage";
import KitSolarConfigPage from "@/pages/Solar/KitSolarConfigPage";
import ExternalCardForm from "@/pages/Solar/ExternalCardForm";
import ConfiguracaoSistemas from "@/pages/Solar/ConfiguracaoSistemas";
import PropostasPage from "@/pages/Solar/PropostasPage";

const queryClient = new QueryClient();

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false} storageKey="vite-ui-theme">
        <BrowserRouter>
          <AuthProvider>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/login" element={<Login />} />
              <Route path="/cadastro" element={<Cadastro />} />
              <Route path="/dashboard" element={<Dashboard />}>
                <Route index element={<GeneralDashboard />} />
              </Route>
              
              {/* Energia Routes */}
              <Route path="/energia" element={<EnergiaDashboard />} />
              <Route path="/energia/configuracao" element={<EnergiaConfiguracao />} />
              <Route path="/energia/monitoramento" element={<MonitoramentoPage />} />
              <Route path="/energia/api" element={<InverterApiPage />} />
              
              {/* Financeiro Routes */}
              <Route path="/financeiro" element={<FinanceiroDashboard />} />
              <Route path="/financeiro/gerador/:id" element={<FinanceiroGeradorPage />} />
              <Route path="/financeiro/contratos" element={<FinanceiroFormPage />} />
              <Route path="/financeiro/recebimentos/:tipo" element={<FinanceiroRecebimentosPage />} />
              <Route path="/financeiro/pagamentos/:tipo" element={<FinanceiroPagamentosPage />} />
              <Route path="/financeiro/cadastro/:tipo" element={<FinanceiroCadastroPage />} />
              <Route path="/financeiro/configuracoes" element={<FinanceiroConfiguracoesPage />} />
              <Route path="/financeiro/contas" element={<FinanceiroDashboard />} />
              <Route path="/financeiro/relatorios" element={<RelatoriosFinanceirosPage />} />
              
              {/* Geradores Routes */}
              <Route path="/geradores" element={<GeradoresPage />} />
              <Route path="/geradores/novo" element={<GeradorNovo />} />
              <Route path="/geradores/:id" element={<GeradorDetalhes />} />
              
              {/* Clientes Routes */}
              <Route path="/clientes" element={<ClientesPage />} />
              <Route path="/clientes/novo" element={<ClienteNovo />} />
              <Route path="/clientes/:id" element={<ClienteDetalhes />} />
              
              {/* Parceiros Routes */}
              <Route path="/parceiros" element={<ParceirosPage />} />
              <Route path="/parceiros/:id" element={<ParceiroDetalhes />} />
              
              {/* Users Routes - updated path from /users to /usuarios */}
              <Route path="/usuarios" element={<UsersPage />} />
              <Route path="/perfil" element={<ProfilePage />} />
              
              {/* Solar Routes */}
              <Route path="/solar" element={<SolarDashboard />} />
              <Route path="/solar/fluxo" element={<FluxoSolar />} />
              <Route path="/solar/calculadora" element={<CalculadoraSolarPage />} />
              <Route path="/solar/propostas" element={<PropostasPage />} />
              <Route path="/solar/configuracao" element={<KitSolarConfigPage />} />
              <Route path="/solar/configuracao/sistemas" element={<ConfiguracaoSistemas />} />
              <Route path="/solar/api" element={<InverterApiPage />} />
              
              {/* Changed from /solar/card/:id to /checklist */}
              <Route path="/checklist" element={<ExternalCardForm />} />
              <Route path="/form/card/:id" element={<ExternalCardForm />} />
              
              {/* 404 Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
            
            <Toaster />
          </AuthProvider>
        </BrowserRouter>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
